import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { euro, downloadFile } from "../lib/api";
import DateRange, { rangeParams } from "../components/DateRange";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, Euro, Percent, Bell, ArrowUpRight, FileText, FileSpreadsheet, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function KpiCard({ label, value, icon: Icon, tone = "brand", testid, sub }) {
  return (
    <div data-testid={testid} className="bg-white border border-stone-200 p-6 rounded-sm animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{label}</span>
        <Icon size={18} strokeWidth={1.5} className={tone === "alert" ? "text-alert" : "text-brand"} />
      </div>
      <div className="font-serif text-4xl font-light text-stone-900">{value}</div>
      {sub && <p className="text-xs text-stone-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [mcomp, setMcomp] = useState(null);
  const [compMode, setCompMode] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats", { params: rangeParams(from, to) }).then(({ data }) => setStats(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    const url = compMode === "month" ? "/reports/month-comparison" : "/reports/quarter-comparison";
    api.get(url).then(({ data }) => setMcomp(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compMode]);

  const moversUp = useMemo(() => (mcomp?.by_category || []).filter((c) => c.diff > 0), [mcomp]);
  const moversDown = useMemo(() => (mcomp?.by_category || []).filter((c) => c.diff < 0), [mcomp]);

  const doExport = async (kind) => {
    setExporting(true);
    try {
      if (kind === "excel") await downloadFile("/export/excel", rangeParams(from, to), "report_costi.xlsx");
      else await downloadFile("/export/pdf", rangeParams(from, to), "report_costi.pdf");
      toast.success("Report scaricato");
    } catch { toast.error("Errore durante l'esportazione"); }
    finally { setExporting(false); }
  };

  if (!stats) return <div className="text-stone-400 font-serif text-2xl">Caricamento…</div>;

  const fc = stats.food_cost_pct;
  const fcTone = fc == null ? "text-stone-400" : fc <= 30 ? "text-brand" : fc <= 40 ? "text-brand" : "text-alert";

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Il tuo locale</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Panoramica</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
          <button data-testid="export-excel-btn" disabled={exporting} onClick={() => doExport("excel")}
            className="flex items-center gap-2 rounded-none border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-white transition-colors disabled:opacity-60">
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button data-testid="export-pdf-btn" disabled={exporting} onClick={() => doExport("pdf")}
            className="flex items-center gap-2 rounded-none border border-alert text-alert px-4 py-2 text-sm font-semibold hover:bg-alert hover:text-white transition-colors disabled:opacity-60">
            <FileDown size={16} /> PDF
          </button>
        </div>
      </div>

      {stats.incidence_alerts?.length > 0 && (
        <div data-testid="overview-incidence-banner" className="bg-alert/10 border border-alert/30 rounded-sm p-4 flex items-start gap-3 animate-fade-in-up">
          <AlertTriangle size={18} className="text-alert mt-0.5 shrink-0" />
          <div className="text-sm text-stone-700">
            <b className="text-alert">Incidenza costi oltre la soglia del {stats.food_cost_threshold}%.</b> Mesi interessati: {stats.incidence_alerts.map((a) => `${a.month} (${a.incidence}%)`).join(", ")}
          </div>
        </div>
      )}

      {stats.price_rise_count > 0 && (
        <Link to="/avvisi" data-testid="overview-rincaro-banner" className="block bg-amber-50 border border-amber-300 rounded-sm p-4 hover:bg-amber-100 transition-colors animate-fade-in-up">
          <div className="flex items-start gap-3">
            <TrendingUp size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-stone-700 flex-1">
              <b className="text-amber-700">{stats.price_rise_count} {stats.price_rise_count === 1 ? "prodotto" : "prodotti"} in rincaro</b> sopra la media storica d'acquisto. Controlla gli avvisi prezzi.
            </div>
            <ArrowUpRight size={16} className="text-amber-600 shrink-0" />
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-costs" label="Costi totali" value={euro(stats.total_costs)} icon={Euro} sub={`${stats.invoice_count} fatture`} />
        <KpiCard testid="kpi-sales" label="Vendite totali" value={euro(stats.total_sales)} icon={TrendingUp} />
        <div data-testid="kpi-foodcost" className="bg-white border border-stone-200 p-6 rounded-sm animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Incidenza costi</span>
            <Percent size={18} strokeWidth={1.5} className={fcTone} />
          </div>
          <div className={`font-serif text-4xl font-light ${fcTone}`}>{fc == null ? "—" : `${fc}%`}</div>
          <p className="text-xs text-stone-400 mt-1">Food cost sul fatturato</p>
        </div>
        <KpiCard testid="kpi-alerts" label="Avvisi attivi" value={stats.active_alerts} icon={Bell} tone={stats.active_alerts > 0 ? "alert" : "brand"} sub="Aumenti di prezzo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 p-6 rounded-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl text-stone-900">Costi vs Vendite</h3>
            <span className="text-xs text-stone-400">per mese</span>
          </div>
          {stats.series.length === 0 ? (
            <Empty text="Carica le fatture e inserisci le vendite per vedere l'andamento." />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={stats.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="month" stroke="#A8A29E" fontSize={12} />
                <YAxis stroke="#A8A29E" fontSize={12} />
                <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
                <Legend />
                <Line type="monotone" dataKey="costs" name="Costi" stroke="#8B3A3A" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="sales" name="Vendite" stroke="#2C4A3B" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-stone-200 p-6 rounded-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl text-stone-900">Aumenti recenti</h3>
            <Link to="/avvisi" className="text-xs text-brand hover:underline">Tutti</Link>
          </div>
          {stats.top_alerts.length === 0 ? (
            <Empty text="Nessun aumento di prezzo rilevato." />
          ) : (
            <div className="space-y-3">
              {stats.top_alerts.map((a, i) => (
                <div key={`${a.product}-${a.supplier}-${i}`} data-testid={`overview-alert-${i}`} className="flex items-start gap-3 pb-3 border-b border-stone-100 last:border-0">
                  <div className="mt-0.5 w-8 h-8 rounded-sm bg-alert/10 flex items-center justify-center shrink-0">
                    <ArrowUpRight size={16} className="text-alert" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-stone-800 truncate">{a.product}</p>
                    <p className="text-xs text-stone-500 truncate">{a.supplier}</p>
                  </div>
                  <span className="text-sm font-bold text-alert shrink-0">+{a.change_pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {mcomp && (
        <div data-testid="month-comparison" className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-2xl text-stone-900">Confronto {compMode === "month" ? "mensile" : "trimestrale"}</h3>
              <div className="flex rounded-full border border-stone-200 overflow-hidden text-xs">
                <button data-testid="comp-mode-month" onClick={() => setCompMode("month")} className={`px-3 py-1 transition-colors ${compMode === "month" ? "bg-brand text-white" : "text-stone-500 hover:bg-stone-100"}`}>Mese</button>
                <button data-testid="comp-mode-quarter" onClick={() => setCompMode("quarter")} className={`px-3 py-1 transition-colors ${compMode === "quarter" ? "bg-brand text-white" : "text-stone-500 hover:bg-stone-100"}`}>Trimestre</button>
              </div>
            </div>
            <span className="text-xs text-stone-400">{mcomp.month || mcomp.quarter} vs {mcomp.prev_month || mcomp.prev}</span>
          </div>
          <div className="flex items-end gap-10 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-stone-500">{compMode === "month" ? "Questo mese" : "Questo trimestre"}</p>
              <p className="font-serif text-4xl font-light text-stone-900">{euro(mcomp.current)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-stone-500">Periodo prec. ({mcomp.prev_month || mcomp.prev})</p>
              <p className="font-serif text-4xl font-light text-stone-500">{euro(mcomp.previous)}</p>
            </div>
            {mcomp.diff_pct != null && (
              <div className={`flex items-center gap-1 ${mcomp.diff > 0 ? "text-alert" : "text-brand"}`}>
                {mcomp.diff > 0 ? <TrendingUp size={22} /> : <TrendingDown size={22} />}
                <span className="font-serif text-4xl font-light">{mcomp.diff > 0 ? "+" : ""}{mcomp.diff_pct}%</span>
              </div>
            )}
          </div>
          {mcomp.by_category.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 pt-6 border-t border-stone-100">
              <div data-testid="movers-up">
                <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Reparti in aumento</p>
                {moversUp.slice(0, 4).map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-1 text-sm">
                    <span className="text-stone-700">{c.category}</span>
                    <span className="text-alert font-semibold">+{euro(c.diff)}{c.diff_pct != null ? ` (+${c.diff_pct}%)` : ""}</span>
                  </div>
                ))}
                {moversUp.length === 0 && <p className="text-sm text-stone-400">Nessuno</p>}
              </div>
              <div data-testid="movers-down">
                <p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Reparti in calo</p>
                {moversDown.slice(-4).reverse().map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-1 text-sm">
                    <span className="text-stone-700">{c.category}</span>
                    <span className="text-brand font-semibold">{euro(c.diff)}{c.diff_pct != null ? ` (${c.diff_pct}%)` : ""}</span>
                  </div>
                ))}
                {moversDown.length === 0 && <p className="text-sm text-stone-400">Nessuno</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="h-48 flex flex-col items-center justify-center text-center gap-2 text-stone-400">
      <FileText size={28} strokeWidth={1.3} />
      <p className="text-sm max-w-[220px]">{text}</p>
    </div>
  );
}
